import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import {
  LEAD_STATUSES,
  LEAD_STATUS_LABELS,
  type LeadWithRelations,
  type Trip,
  type Destination,
  type User,
} from "@ckgo2/shared/types";
import { formatDate } from "@ckgo2/shared/utils";
import { LEAD_STATUS_CONFIG } from "@/lib/statusConfig";
import { useEditPage } from "@/hooks/useEditPage";
import CommunicationTimeline from "@/components/orders/CommunicationTimeline";
import RecentCallsPanel from "@/components/common/RecentCallsPanel";
import AiReviewPanel, { type ParseResult } from "@/components/common/AiReviewPanel";
import { useAgent } from "@/components/agent/AgentProvider";
import { ArrowLeft, Loader2, Save, ShoppingCart, Sparkles, Trash2 } from "lucide-react";
import type { Communication } from "@ckgo2/shared/types";

// Enriched qualification chips (Block 5.7 Řez 2 — Veru's wishlist fields).
const PROFILE_LABELS: Record<string, Record<string, string>> = {
  tripType: { fit: "Tailor-made", guided: "Guided tour", stay: "Resort stay", service: "Service" },
  flightClass: { economy: "Economy", premium: "Premium eco", business: "Business" },
  dateFlexibility: { fixed: "Fixed dates", plus_minus_week: "± a week", flexible: "Flexible" },
  travelExperience: {
    first_asia: "First time in Asia",
    experienced: "Asia-experienced",
    expert: "Seasoned traveller",
  },
};

interface LeadForm {
  status: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  tripId: string;
  destinationId: string;
  assignedTo: string;
  requestedDate: string;
  paxAdults: number;
  paxChildren: number;
  estimatedValue: number | null;
  currency: string;
  nextActionAt: string;
  lostReason: string;
  customerNotes: string;
  internalNotes: string;
}

const EMPTY: LeadForm = {
  status: "received",
  contactName: "",
  contactEmail: "",
  contactPhone: "",
  tripId: "",
  destinationId: "",
  assignedTo: "",
  requestedDate: "",
  paxAdults: 2,
  paxChildren: 0,
  estimatedValue: null,
  currency: "CZK",
  nextActionAt: "",
  lostReason: "",
  customerNotes: "",
  internalNotes: "",
};

type LeadDetail = LeadWithRelations & { communications?: Communication[] };

export default function LeadDetailPage() {
  const { openChat, enabled: agentEnabled } = useAgent();
  // "Use as reply" on the AI first-reply card seeds the timeline's reply composer.
  const [prefillReply, setPrefillReply] = useState<string | undefined>();

  // Edit/create plumbing (params, query, form, save mutation, navigation) via the shared hook.
  // stayOnSave keeps us on the lead after save (create navigates to the new lead's detail).
  const {
    isNew,
    isLoading,
    form,
    saveMutation: save,
    navigate,
    itemData: lead,
  } = useEditPage<LeadForm, LeadDetail>({
    resourceKey: "lead",
    listKey: "leads",
    apiPath: "/api/admin/leads",
    navigateTo: "/leads",
    defaultValues: EMPTY,
    stayOnSave: true,
    transformOnLoad: (l) => ({
      status: l.status,
      contactName: l.contactName ?? "",
      contactEmail: l.contactEmail ?? "",
      contactPhone: l.contactPhone ?? "",
      tripId: l.tripId ?? "",
      destinationId: l.destinationId ?? "",
      assignedTo: l.assignedTo ?? "",
      requestedDate: l.requestedDate ?? "",
      paxAdults: l.paxAdults,
      paxChildren: l.paxChildren,
      estimatedValue: l.estimatedValue ?? null,
      currency: l.currency,
      nextActionAt: l.nextActionAt ?? "",
      lostReason: l.lostReason ?? "",
      customerNotes: l.customerNotes ?? "",
      internalNotes: l.internalNotes ?? "",
    }),
    transformOnSave: (data) => ({
      ...data,
      estimatedValue: data.estimatedValue === null ? null : Number(data.estimatedValue),
      paxAdults: Number(data.paxAdults),
      paxChildren: Number(data.paxChildren),
    }),
  });
  const { register, handleSubmit, watch, setValue } = form;

  // Hard delete (junk leads — mailer-daemon, spam). FKs on communications/orders are
  // `set null`, so linked history survives; only the lead record goes away.
  const queryClient = useQueryClient();
  const deleteLead = useMutation({
    mutationFn: () => api.delete(`/api/admin/leads/${lead?.id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      navigate("/leads");
    },
  });

  // Apply an AI parse result to the form (only non-empty values; never clobbers).
  const applyAi = (r: ParseResult) => {
    const f = r.fields;
    const setStr = (name: keyof LeadForm, v: unknown) => {
      if (v !== "" && v != null) setValue(name, v as never);
    };
    setStr("contactName", f.contactName?.value);
    setStr("contactEmail", f.contactEmail?.value);
    setStr("contactPhone", f.contactPhone?.value);
    setStr("tripId", f.tripId?.value);
    setStr("destinationId", f.destinationId?.value);
    setStr("requestedDate", f.requestedDate?.value);
    setStr("customerNotes", f.notes?.value);
    if (f.paxAdults?.value) setValue("paxAdults", Number(f.paxAdults.value));
    if (f.paxChildren?.value) setValue("paxChildren", Number(f.paxChildren.value));
  };

  const { data: usersData } = useQuery({
    queryKey: ["users"],
    queryFn: () => api.get<ApiResponse<User[]>>("/api/admin/users"),
  });
  const { data: tripsData } = useQuery({
    queryKey: ["trips-for-lead"],
    queryFn: () => api.get<ApiResponse<Trip[]>>("/api/admin/trips"),
  });
  const { data: destData } = useQuery({
    queryKey: ["destinations"],
    queryFn: () => api.get<ApiResponse<Destination[]>>("/api/admin/destinations"),
  });

  const status = watch("status");
  // The lead carries its own currency (resolved server-side from finance.defaultCurrency) — show it
  // instead of a hardcoded "CZK", which was misleading on a USD-priced lead.
  const currency = watch("currency") || "USD";

  if (!isNew && isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="text-primary-600 h-8 w-8 animate-spin" />
      </div>
    );
  }

  const cfg =
    LEAD_STATUS_CONFIG[status as keyof typeof LEAD_STATUS_CONFIG] ?? LEAD_STATUS_CONFIG.received;
  const users = usersData?.data ?? [];
  const trips = tripsData?.data ?? [];
  const destinations = destData?.data ?? [];

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/leads")} className="btn-ghost btn-sm">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {isNew ? "New lead" : lead?.leadNumber}
              </h1>
              {!isNew && <span className={`badge ${cfg.className}`}>{cfg.label}</span>}
            </div>
            {!isNew && lead && (
              <p className="text-sm text-gray-500">Created {formatDate(lead.createdAt)}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isNew && lead && (
            <button
              type="button"
              onClick={() => {
                if (
                  window.confirm(
                    `Delete lead ${lead.leadNumber}? Linked messages and orders keep their history — only the lead record is removed.`,
                  )
                ) {
                  deleteLead.mutate();
                }
              }}
              disabled={deleteLead.isPending}
              className="inline-flex items-center gap-2 rounded-lg border border-red-200 px-3 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20"
            >
              {deleteLead.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              Delete
            </button>
          )}
          {!isNew && agentEnabled && (
            <button
              type="button"
              onClick={() =>
                openChat(
                  "Build a trip offer for this inquiry: suggest a fitting departure with real availability and a price estimate from our catalog, then draft a short pitch I can send. Use only grounded data.",
                )
              }
              className="border-primary-200 text-primary-700 hover:bg-primary-50 dark:border-primary-800 dark:text-primary-300 dark:hover:bg-primary-900/20 inline-flex items-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium"
            >
              <Sparkles className="h-4 w-4" />
              Build offer
            </button>
          )}
          <button
            onClick={handleSubmit((d) => save.mutate(d))}
            disabled={save.isPending}
            className="btn-primary"
          >
            {save.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Save
          </button>
        </div>
      </div>

      {save.isSuccess && !isNew && (
        <div className="rounded-lg bg-green-50 px-4 py-2 text-sm text-green-700 dark:bg-green-900/30 dark:text-green-400">
          Saved
        </div>
      )}

      {/* AI assist — only when creating a new lead from a pasted inquiry. */}
      {isNew && <AiReviewPanel onApply={applyAi} />}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main form */}
        <form className="space-y-6 lg:col-span-2">
          <div className="card space-y-4">
            <h2 className="font-semibold text-gray-900 dark:text-white">Contact</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Name">
                <input className="input" {...register("contactName")} />
              </Field>
              <Field label="Email">
                <input className="input" type="email" {...register("contactEmail")} />
              </Field>
              <Field label="Phone">
                <input className="input" {...register("contactPhone")} />
              </Field>
              <Field label="Assigned agent">
                <select className="input" {...register("assignedTo")}>
                  <option value="">— Unassigned —</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          </div>

          <div className="card space-y-4">
            <h2 className="font-semibold text-gray-900 dark:text-white">Interest</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Trip">
                <select className="input" {...register("tripId")}>
                  <option value="">— None / custom —</option>
                  {trips.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.title}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Destination">
                <select className="input" {...register("destinationId")}>
                  <option value="">— None —</option>
                  {destinations.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Requested date">
                <input className="input" type="date" {...register("requestedDate")} />
              </Field>
              <Field label={`Estimated value (${currency})`}>
                <input
                  className="input"
                  type="number"
                  {...register("estimatedValue", { valueAsNumber: true })}
                />
              </Field>
              <Field label="Adults">
                <input
                  className="input"
                  type="number"
                  min={1}
                  {...register("paxAdults", { valueAsNumber: true })}
                />
              </Field>
              <Field label="Children">
                <input
                  className="input"
                  type="number"
                  min={0}
                  {...register("paxChildren", { valueAsNumber: true })}
                />
              </Field>
            </div>
          </div>

          <div className="card space-y-4">
            <h2 className="font-semibold text-gray-900 dark:text-white">Pipeline</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Status">
                <select className="input" {...register("status")}>
                  {LEAD_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {LEAD_STATUS_LABELS[s]}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Next action (callback)">
                <input className="input" type="date" {...register("nextActionAt")} />
              </Field>
              {status === "lost" && (
                <Field label="Lost reason" full>
                  <input className="input" {...register("lostReason")} />
                </Field>
              )}
            </div>
            <Field label="Customer notes" full>
              <textarea className="input resize-none" rows={2} {...register("customerNotes")} />
            </Field>
            <Field label="Internal notes" full>
              <textarea className="input resize-none" rows={2} {...register("internalNotes")} />
            </Field>
          </div>
        </form>

        {/* Sidebar */}
        <div className="space-y-6">
          {!isNew && lead && (
            <div className="card space-y-3">
              <h2 className="font-semibold text-gray-900 dark:text-white">Conversion</h2>
              {lead.convertedOrderId ? (
                <Link
                  to={`/orders/${lead.convertedOrderId}`}
                  className="btn-ghost w-full justify-center"
                >
                  <ShoppingCart className="h-4 w-4" />
                  View order
                </Link>
              ) : (
                <Link
                  to={`/orders/new?leadId=${lead.id}`}
                  className="btn-primary w-full justify-center"
                >
                  <ShoppingCart className="h-4 w-4" />
                  Create order
                </Link>
              )}
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Lead score</span>
                <span className="flex items-center gap-1 font-medium text-gray-900 dark:text-white">
                  <Sparkles className="h-3.5 w-3.5 text-amber-500" />
                  {lead.leadScore}
                </span>
              </div>
            </div>
          )}

          {!isNew && lead?.profile && Object.keys(lead.profile).length > 0 && (
            <div className="card">
              <h2 className="mb-2 font-semibold text-gray-900 dark:text-white">Trip profile</h2>
              <div className="flex flex-wrap gap-1.5 text-xs">
                {lead.profile.tripType && (
                  <span className="rounded-full bg-purple-100 px-2 py-0.5 font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                    {PROFILE_LABELS.tripType[lead.profile.tripType] ?? lead.profile.tripType}
                  </span>
                )}
                {lead.profile.flightClass && (
                  <span className="rounded-full bg-blue-100 px-2 py-0.5 font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                    {PROFILE_LABELS.flightClass[lead.profile.flightClass] ??
                      lead.profile.flightClass}
                  </span>
                )}
                {lead.profile.budget && (
                  <span className="rounded-full bg-green-100 px-2 py-0.5 font-medium text-green-700 dark:bg-green-900/30 dark:text-green-300">
                    {lead.profile.budget}
                  </span>
                )}
                {lead.profile.dateFlexibility && (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                    {PROFILE_LABELS.dateFlexibility[lead.profile.dateFlexibility] ??
                      lead.profile.dateFlexibility}
                  </span>
                )}
                {lead.profile.travelExperience && (
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                    {PROFILE_LABELS.travelExperience[lead.profile.travelExperience] ??
                      lead.profile.travelExperience}
                  </span>
                )}
                {Array.isArray(lead.profile.childAges) && lead.profile.childAges.length > 0 && (
                  <span className="rounded-full bg-pink-100 px-2 py-0.5 font-medium text-pink-700 dark:bg-pink-900/30 dark:text-pink-300">
                    Children: {lead.profile.childAges.join(", ")}
                  </span>
                )}
              </div>
              {lead.profile.routing && (
                <p className="mt-2 text-xs text-gray-400">
                  Auto-routed via{" "}
                  {lead.profile.routing.by === "garant" ? "destination garant" : "round-robin"}
                </p>
              )}
            </div>
          )}

          {!isNew && lead && (
            <div className="card">
              <h2 className="mb-2 font-semibold text-gray-900 dark:text-white">Attribution</h2>
              <dl className="space-y-1 text-sm">
                <Row label="Source" value={lead.source} />
                <Row label="Campaign" value={lead.utmCampaign || lead.firstTouchCampaign} />
                <Row label="Medium" value={lead.utmMedium} />
                <Row label="Click ID" value={lead.clickId} />
              </dl>
            </div>
          )}

          {!isNew && lead?.aiDraft?.draft && (
            <div className="card border-primary-200 dark:border-primary-900/40 border">
              <div className="mb-2 flex items-center gap-2">
                <Sparkles className="text-primary-600 dark:text-primary-400 h-5 w-5" />
                <h2 className="font-semibold text-gray-900 dark:text-white">
                  AI-suggested first reply
                </h2>
              </div>
              <p className="whitespace-pre-wrap rounded-lg bg-gray-50 p-3 text-sm text-gray-700 dark:bg-gray-800/50 dark:text-gray-300">
                {lead.aiDraft.draft}
              </p>
              <div className="mt-2 flex justify-end">
                <button
                  type="button"
                  onClick={() => setPrefillReply(lead.aiDraft?.draft)}
                  disabled={!lead.contactEmail}
                  className="btn-primary btn-sm disabled:opacity-50"
                  title={!lead.contactEmail ? "No contact email on this lead" : undefined}
                >
                  <Sparkles className="h-4 w-4" /> Use as reply
                </button>
              </div>
            </div>
          )}

          {!isNew && lead && (
            <RecentCallsPanel
              communications={lead.communications ?? []}
              invalidateKeys={[["lead", lead.id]]}
            />
          )}

          {!isNew && lead && (
            <div className="card">
              <CommunicationTimeline
                leadId={lead.id}
                customerId={lead.customerId ?? ""}
                communications={lead.communications ?? []}
                contactEmail={lead.contactEmail ?? ""}
                prefillReply={prefillReply}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
  full,
}: {
  label: string;
  children: React.ReactNode;
  full?: boolean;
}) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="mb-1 block text-sm font-medium text-gray-600 dark:text-gray-400">
        {label}
      </label>
      {children}
    </div>
  );
}

function Row({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-gray-500">{label}</dt>
      <dd className="font-medium text-gray-900 dark:text-white">{value || "—"}</dd>
    </div>
  );
}
